import { acceptConsentsSchema } from "@closer/shared";
import { Button } from "@closer/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { LegalConsentFields, type LegalDocument, usePendingConsents } from "./LegalConsentFields";

/**
 * Yeniden onay ekranı (CLOS-343).
 *
 * Kullanıcı zorunlu bir metnin yeni sürümünü onaylayana kadar ürüne giremez. Bu ekran o
 * kapının TEK çıkışı; bu yüzden burada gösterilen liste sunucunun "eksik" dediğiyle birebir
 * aynı olmalı. İstemci kendi başına "şu da lazımdır" diye ekleme yapmaz — yaparsa kullanıcı
 * onaylar, sunucu yine reddeder ve çıkışı olmayan bir döngü oluşur.
 */
export function ReconsentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const eksikSorgusu = usePendingConsents();
  const { data: eksikler, isLoading } = eksikSorgusu;
  const [secilenler, setSecilenler] = useState<string[]>([]);
  const [hata, setHata] = useState<string | undefined>();

  /**
   * Onaylamak İSTEMEYEN kullanıcının çıkışı (CLOS-496 sınıfı, bug avı).
   *
   * Bu ekran her korumalı rotanın önünde ve "Onaylıyorum" dışında bir yol yoktu:
   * metni kabul etmeyen kullanıcı ekranda kilitleniyordu (üst menü de yok, tarayıcı
   * geri tuşu kapıyı yeniden tetikliyor). Router'ın yorumu bu ekranın onay kapısının
   * DIŞINDA olma sebebini "kullanıcı kendi çıkışına ulaşabilsin" diye açıklıyordu —
   * ama çıkışın kendisi eksikti.
   */
  const cikisYap = async (): Promise<void> => {
    try {
      await logout();
      navigate("/login");
    } catch {
      setHata("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  const kaydet = useMutation({
    mutationFn: async (ids: string[]) => {
      // Şema `@closer/shared`'dan: istemci kendi kuralını yazmaz, sunucuyla aynı şemayı
      // kullanır (kriter 6).
      const govde = acceptConsentsSchema.parse({ acceptedDocumentIds: ids });
      await api("/api/session/consents", { method: "POST", body: JSON.stringify(govde) });
    },
    onSuccess: async () => {
      // Kapı bu sorgunun sonucuna bakıyor; tazelenmeden yönlendirirsek kullanıcı buraya
      // geri savrulur.
      await queryClient.invalidateQueries({ queryKey: ["session", "pending-consents"] });
      void navigate("/", { replace: true });
    },
    onError: (err: unknown) => {
      setHata(err instanceof Error ? err.message : "Onaylar kaydedilemedi");
    },
  });

  if (isLoading) return null;

  const belgeler: LegalDocument[] = eksikler ?? [];
  const zorunlular = belgeler.filter((b) => b.required).map((b) => b.id);
  /**
   * ⚠ LİSTE ALINAMAZSA ONAY GÖNDERİLMİYOR (kusur avı, 26 Ağustos).
   *
   * Sorgu hata verdiğinde `belgeler` boş kalıyor, "zorunlu kutu var mı" kontrolü de boş
   * geçiyor ve düğme AÇIK kalıyordu. Kullanıcı boş bir onay gönderiyor, kapı hâlâ eksik
   * görüyor ve buraya geri savruluyordu — dosyanın başında yasaklanan çıkışsız döngünün
   * ta kendisi.
   */
  const listeAlinamadi = eksikSorgusu.error !== null;
  const eksikZorunlu = listeAlinamadi || zorunlular.some((id) => !secilenler.includes(id));

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">Güncellenen metinler</h1>
        <p className="text-sm text-fg-muted">
          Aşağıdaki metinler güncellendi. Devam etmek için yeni sürümleri onaylamanız gerekiyor.
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setHata(undefined);
          kaydet.mutate(secilenler);
        }}
      >
        <LegalConsentFields
          belgeler={belgeler}
          secilenler={secilenler}
          degistir={(id, secili) =>
            setSecilenler((onceki) =>
              secili ? [...onceki, id] : onceki.filter((mevcut) => mevcut !== id),
            )
          }
          hata={hata}
          yuklenemedi={listeAlinamadi}
          yenidenDene={() => void eksikSorgusu.refetch()}
        />

        {/* Kutular işaretlenmeden düğme kapalı: sunucuya 422 aldırmadan önce kullanıcıya
            neyin eksik olduğunu ekranda göstermek daha az can sıkıcı. */}
        <Button type="submit" disabled={eksikZorunlu || kaydet.isPending}>
          {kaydet.isPending ? "Kaydediliyor…" : "Onaylıyorum"}
        </Button>
      </form>

      <p className="text-center text-sm text-fg-soft">
        Bu metinleri onaylamak istemiyorsanız{" "}
        <Button variant="ghost" onClick={() => void cikisYap()}>
          çıkış yapabilirsiniz
        </Button>
      </p>
    </main>
  );
}
